import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { projectApi, leadApi, clientApi, teamApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save, UserCheck, User } from 'lucide-react';
import { toast } from 'sonner';

const PROJECT_TYPES = [
  'Kitchen Remodel',
  'Bathroom Remodel',
  'Home Addition',
  'Whole House Renovation',
  'Basement Finishing',
  'Deck/Patio',
  'Roofing',
  'Siding',
  'Windows/Doors',
  'New Construction',
  'Other',
];
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];
const PROJECT_STATUSES = ['active', 'completed', 'on_hold'];

export default function ProjectForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('lead_id');
  const clientIdFromUrl = searchParams.get('client_id');
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing || Boolean(leadId) || Boolean(clientIdFromUrl));
  const [prefilledFromClient, setPrefilledFromClient] = useState(false);
  const [existingClient, setExistingClient] = useState(null);
  const [managers, setManagers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    lead_id: leadId || '',
    client_id: clientIdFromUrl || '',
    client_name: '',
    client_email: '',
    client_phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    project_type: '',
    contract_value: '',
    start_date: '',
    estimated_end_date: '',
    description: '',
    status: 'active',
    assigned_manager_id: '',
  });

  useEffect(() => {
    if (isEditing) {
      fetchProject();
    } else if (leadId) {
      fetchLead();
    } else if (clientIdFromUrl) {
      fetchClientAndPrefill(clientIdFromUrl);
    } else {
      setFetching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, leadId, clientIdFromUrl]);

  // Load assignable users (owner + managers); default new projects to the owner
  useEffect(() => {
    teamApi.getMembers().then(res => {
      const assignable = res.data.filter(m => m.role === 'owner' || m.role === 'manager');
      assignable.sort((a, b) => (a.role === 'owner' ? -1 : b.role === 'owner' ? 1 : 0));
      setManagers(assignable);
      if (!isEditing) {
        const owner = assignable.find(m => m.role === 'owner');
        if (owner) {
          setFormData(prev => (prev.assigned_manager_id ? prev : { ...prev, assigned_manager_id: owner.id }));
        }
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClientAndPrefill = async (clientId) => {
    try {
      const response = await clientApi.getOne(clientId);
      const client = response.data;
      
      const clientName = client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim();

      setFormData(prev => ({
        ...prev,
        client_id: clientId,
        client_name: clientName,
        client_email: client.email || '',
        client_phone: client.phone || '',
        address: client.address || '',
        city: client.city || '',
        state: client.state || '',
        zip_code: client.zip || client.zip_code || '',
      }));
      
      setExistingClient(client);
      setPrefilledFromClient(true);
      toast.success(`Pre-filled with ${clientName}'s information`);
    } catch (error) {
      console.error('Failed to fetch client:', error);
      toast.error('Could not load client information');
    } finally {
      setFetching(false);
    }
  };

  const fetchProject = async () => {
    try {
      const response = await projectApi.getOne(id);
      setFormData({
        ...response.data,
        contract_value: response.data.contract_value?.toString() || '',
        start_date: response.data.start_date?.split('T')[0] || '',
        estimated_end_date: response.data.estimated_end_date?.split('T')[0] || '',
      });
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setFetching(false);
    }
  };

  const fetchLead = async () => {
    try {
      const response = await leadApi.getOne(leadId);
      const lead = response.data;
      setFormData((prev) => ({
        ...prev,
        lead_id: leadId,
        name: `${lead.project_type || 'Project'} - ${lead.name}`,
        client_name: lead.name,
        client_email: lead.email || '',
        client_phone: lead.phone || '',
        address: lead.address || '',
        city: lead.city || '',
        state: lead.state || '',
        zip_code: lead.zip_code || '',
        project_type: lead.project_type || '',
        contract_value: lead.estimated_value?.toString() || '',
      }));
    } catch (error) {
      toast.error('Failed to load lead data');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.client_name.trim() || !formData.address.trim()) {
      toast.error('Name, client name, and address are required');
      return;
    }
    setLoading(true);

    // Convert empty strings to null for optional fields
    const payload = {
      ...formData,
      client_email: formData.client_email?.trim() || null,
      client_phone: formData.client_phone?.trim() || null,
      city: formData.city?.trim() || null,
      state: formData.state || null,
      zip_code: formData.zip_code?.trim() || null,
      project_type: formData.project_type || null,
      description: formData.description?.trim() || null,
      lead_id: formData.lead_id || null,
      client_id: formData.client_id || null,
      contract_value: formData.contract_value ? parseFloat(formData.contract_value) : null,
      start_date: formData.start_date || null,
      estimated_end_date: formData.estimated_end_date || null,
      assigned_manager_id: formData.assigned_manager_id || null,
    };

    try {
      if (isEditing) {
        await projectApi.update(id, payload);
        toast.success('Project updated');
        navigate(`/projects/${id}`);
      } else {
        const response = await projectApi.create(payload);
        toast.success('Project created');
        navigate(`/projects/${response.data.id}`);
      }
    } catch (error) {
      // 1:1 guard: lead already converted -> silently go to the existing project
      const errorDetail = error.response?.data?.detail;
      if (errorDetail && typeof errorDetail === 'object' && !Array.isArray(errorDetail) && errorDetail.project_id) {
        navigate(`/projects/${errorDetail.project_id}`);
        return;
      }
      // Handle Pydantic validation errors (array format)
      let errorMessage = 'Failed to save project';
      if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        errorMessage = errorDetail.map(e => e.msg || e.message).join(', ');
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(isEditing ? `/projects/${id}` : '/projects')}
          className="rounded-sm"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditing ? 'Edit Project' : 'New Project'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing 
              ? 'Update project details' 
              : prefilledFromClient && existingClient
                ? `Creating project for ${existingClient.name || formData.client_name}`
                : leadId 
                  ? 'Create project from lead' 
                  : 'Create a new construction project'}
          </p>
        </div>
      </div>

      {/* Show notice when prefilled from existing client */}
      {prefilledFromClient && existingClient && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-6">
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-900">Pre-filled from existing client</p>
              <p className="text-sm text-blue-700">
                Client information has been auto-populated. You can edit if needed.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Info */}
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg">Project Information</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="name">Project Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Kitchen Renovation - Smith Residence"
                    className="rounded-sm mt-1"
                    required
                    data-testid="project-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="project_type">Project Type</Label>
                  <Select
                    value={formData.project_type}
                    onValueChange={(val) => handleSelectChange('project_type', val)}
                  >
                    <SelectTrigger className="rounded-sm mt-1" data-testid="project-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isEditing && (
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(val) => handleSelectChange('status', val)}
                    >
                      <SelectTrigger className="rounded-sm mt-1" data-testid="project-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {managers.length > 0 && (
                  <div>
                    <Label htmlFor="assigned_manager">Assigned Manager</Label>
                    <Select
                      value={formData.assigned_manager_id || undefined}
                      onValueChange={(val) => handleSelectChange('assigned_manager_id', val)}
                    >
                      <SelectTrigger className="rounded-sm mt-1" data-testid="project-manager-select">
                        <SelectValue placeholder="Select a manager" />
                      </SelectTrigger>
                      <SelectContent>
                        {managers.map((manager) => (
                          <SelectItem key={manager.id} value={manager.id}>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4" />
                              {manager.name}{manager.role === 'owner' ? ' (Owner)' : ''}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Project scope and details..."
                    rows={3}
                    className="rounded-sm mt-1"
                    data-testid="project-description-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Client Info */}
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg">Client Information</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="client_name">Client Name *</Label>
                  <Input
                    id="client_name"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="rounded-sm mt-1"
                    required
                    data-testid="client-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="client_email">Client Email</Label>
                  <Input
                    id="client_email"
                    name="client_email"
                    type="email"
                    value={formData.client_email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="rounded-sm mt-1"
                    data-testid="client-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="client_phone">Client Phone</Label>
                  <Input
                    id="client_phone"
                    name="client_phone"
                    value={formData.client_phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="rounded-sm mt-1"
                    data-testid="client-phone-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg">Project Address</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="address">Street Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Main Street"
                    className="rounded-sm mt-1"
                    required
                    data-testid="project-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Austin"
                    className="rounded-sm mt-1"
                    data-testid="project-city-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Select
                      value={formData.state}
                      onValueChange={(val) => handleSelectChange('state', val)}
                    >
                      <SelectTrigger className="rounded-sm mt-1" data-testid="project-state-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="zip_code">ZIP Code</Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      placeholder="78701"
                      className="rounded-sm mt-1"
                      data-testid="project-zip-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Financial & Timeline */}
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg">Financial & Timeline</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="contract_value">Contract Value ($)</Label>
                  <Input
                    id="contract_value"
                    name="contract_value"
                    type="number"
                    value={formData.contract_value}
                    onChange={handleChange}
                    placeholder="75000"
                    className="rounded-sm mt-1"
                    data-testid="contract-value-input"
                  />
                </div>
                <div>
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    name="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={handleChange}
                    className="rounded-sm mt-1"
                    data-testid="start-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="estimated_end_date">Est. Completion</Label>
                  <Input
                    id="estimated_end_date"
                    name="estimated_end_date"
                    type="date"
                    value={formData.estimated_end_date}
                    onChange={handleChange}
                    className="rounded-sm mt-1"
                    data-testid="end-date-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 h-11 btn-press"
              data-testid="save-project-btn"
            >
              {loading ? (
                <span className="loading-spinner" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Project' : 'Create Project'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
